import { orderBy } from "lodash";

import { Proposal } from "@fartherfinance/frontend/api/Investment/hooks/useGetProposal";
import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";

import displayTitleFromSecurity from "@src/utils/displayTitleFromSecurity";
import { arrayToCsv } from "@src/yellowstone/modules/shared";

type SecurityDb = Awaited<
  ReturnType<typeof useSecuritiesBySecurityIds>
>["data"];

interface Props {
  proposal: Proposal | undefined;
  securityDb: SecurityDb | undefined;
}

const columns = [
  "Trade No",
  "Account",
  "Ticker",
  "Side",
  "Quantity",
  "Price",
  "Market Value",
] as const satisfies string[];

const prepareProposedTradesCSVArrayData = (props: Props) => {
  const secDb = props.securityDb;
  // Edge-Case: If we have no trades then secDB is undefined and we return an empty array
  if (props.proposal !== undefined && props.proposal.trades.length === 0) {
    return [];
  }

  if (props.proposal === undefined || secDb === undefined) {
    return undefined;
  }

  const orderedTrades = orderBy(
    props.proposal.trades,
    [
      (t) => {
        const security = secDb.find((x) => x.id === t.securityId);
        return security ? displayTitleFromSecurity(security) : "ZZZZ"; // fallback to ZZZZ if security is not defined
      },
      (t) => t.custodianAccountId,
    ],
    ["asc", "asc"]
  );

  return orderedTrades.map((trade, idx) => [
    `Trade ${idx + 1}`,
    trade.custodianAccountId,
    displayTitleFromSecurity(
      props.securityDb?.find((x) => x.id === trade.securityId)
    ),
    trade.side.toUpperCase(),
    trade.quantity.toNumber(),
    trade.price.toNumber(),
    trade.quantity.mul(trade.price).toNumber(),
  ]);
};

const constructCSVDataProposedTrades = (props: Props) => {
  const data = prepareProposedTradesCSVArrayData(props);
  const finalCsv = arrayToCsv([columns, ...(data ?? [])]);
  return finalCsv;
};

export default constructCSVDataProposedTrades;
